/**
 * PATH: packages/ui/src/kanban/components/board-view/cardTileHeight.ts
 * FILE_ID: UI-KANBAN-CARD-TILE-HEIGHT-001
 * PURPOSE: Decide if a card tile is the short face or the tall face.
 * ROLE IN ARCHITECTURE: Pure resolver shared by the board card and the settings preview.
 * MAIN EXPORTS: resolveCardTileHeight, storedCardTileBody
 * NON-RESPONSIBILITIES: Does not open the action rail. Rail mode is a separate control.
 * NOTES FOR FUTURE AI: An unset tile body still follows card density. Explicit Hover
 *   is short until the pointer is on the card. Explicit Expanded stays tall.
 */

export type CardTileDensity = 'compact' | 'expanded' | 'large'
export type CardTileBody = 'rest' | 'open'

export interface CardTileHeight {
  isCompact: boolean
  isLarge: boolean
  shownDensity: CardTileDensity
  /** Extra tile classes. Expanded adds a minimum height so a short title still grows. */
  frameClass: string
}

/**
 * Resolve the tile face from the saved expanded-state choice.
 *
 * Inputs: tileBody is undefined until the operator picks Hover or Expanded.
 * density is the card-size tier. pointerOnCard is pointer presence, not rail mode.
 * Output: compact means the short triage face. shownDensity is the face actually painted.
 * Side effects: none. Failure mode: none. Unknown density falls through as expanded.
 */
export function resolveCardTileHeight(input: {
  tileBody: CardTileBody | undefined
  density: CardTileDensity
  pointerOnCard: boolean
}): CardTileHeight {
  if (input.tileBody === undefined) {
    return {
      isCompact: input.density === 'compact',
      isLarge: input.density === 'large',
      shownDensity: input.density,
      frameClass: '',
    }
  }
  if (input.tileBody === 'open') {
    if (input.density === 'large') {
      return { isCompact: false, isLarge: true, shownDensity: 'large', frameClass: 'min-h-28' }
    }
    return { isCompact: false, isLarge: false, shownDensity: 'expanded', frameClass: 'min-h-28' }
  }
  if (!input.pointerOnCard) {
    return { isCompact: true, isLarge: false, shownDensity: 'compact', frameClass: '' }
  }
  if (input.density === 'large') {
    return { isCompact: false, isLarge: true, shownDensity: 'large', frameClass: '' }
  }
  return { isCompact: false, isLarge: false, shownDensity: 'expanded', frameClass: '' }
}

/**
 * Read the saved Hover/Expanded choice for this board, then the all-boards choice.
 *
 * Inputs: prefs may omit tileBody. boardId selects a this-board override.
 * Output: undefined when the operator has not picked a state yet.
 * Side effects: none.
 */
export function storedCardTileBody(
  prefs: {
    tileBody?: CardTileBody
    boardOverrides?: Record<string, { tileBody?: CardTileBody } | undefined>
  },
  boardId?: string,
): CardTileBody | undefined {
  const override = boardId ? prefs.boardOverrides?.[boardId]?.tileBody : undefined
  return override ?? prefs.tileBody
}
